import { CONVERSATION_FIELDS, isConditionGroup, matchesSegment, type ConditionEntry, type FieldDefinition, type Segment } from './segments'

/**
 * Trigger → conditions → actions, the general automation layer sitting above the
 * industry-specific ticket routing in `workflows.ts`.
 *
 * Deliberately a separate engine rather than an extension of `workflows.ts`: that file is a
 * complete, small, well-tested thing — "which category and SLA does this message get" — and
 * bending it to also mean "run any of thirteen kinds of event through arbitrary conditions
 * and actions" would make both harder to reason about. The condition half is not
 * reinvented either: every trigger reuses `@shared/segments`'s `Condition`/`matchesSegment`,
 * the same engine contacts, saved views, and broadcasts already run on.
 *
 * **Conditions are one flat list here, not the nested AND/OR groups a fuller automation
 * product eventually wants.** Depth beyond one level is a real gap, tracked in HANDOVER.md —
 * building a recursive condition-group UI is a separate piece of work from wiring up thirteen
 * trigger points, and flat conditions cover the overwhelming majority of real rules
 * ("category is Refunds AND priority is urgent") without it.
 */

export type AutomationTrigger =
  | 'chat.created'
  | 'chat.label_added'
  | 'chat.label_removed'
  | 'message.received'
  | 'message.updated'
  | 'message.deleted'
  | 'message.flagged'
  | 'message.unflagged'
  | 'ticket.created'
  | 'ticket.updated'
  | 'ticket.closed'
  | 'ticket.label_added'
  | 'ticket.label_removed'
  | 'task.created'
  | 'task.assigned'
  | 'reaction.received'
  | 'member.joined'
  | 'member.role_changed'

export const TRIGGER_LABELS: Record<AutomationTrigger, string> = {
  'chat.created': 'A new chat starts',
  'chat.label_added': 'A label is added to a chat',
  'chat.label_removed': 'A label is removed from a chat',
  'message.received': 'A customer sends a message',
  'message.updated': 'A message is edited',
  'message.deleted': 'A message is deleted',
  'message.flagged': 'A message is flagged',
  'message.unflagged': 'A message is unflagged',
  'ticket.created': 'A ticket is opened',
  'ticket.updated': 'A ticket is edited (status, priority, category, or assignee)',
  'ticket.closed': 'A ticket is closed',
  'ticket.label_added': 'A label is added to a ticket',
  'ticket.label_removed': 'A label is removed from a ticket',
  'task.created': 'A task is created',
  'task.assigned': 'A task is assigned',
  'reaction.received': 'Someone reacts to a message',
  'member.joined': 'A teammate joins the workspace',
  'member.role_changed': "A teammate's role changes",
}

/**
 * Which field catalogue a trigger's conditions are checked against. Ticket triggers use
 * `conversation` too — `CONVERSATION_FIELDS` already carries the ticket's own status,
 * priority, and category, since a ticket is always read through the chat it belongs to.
 */
export type AutomationEntity = 'conversation' | 'message' | 'task' | 'reaction' | 'member'

export const TRIGGER_ENTITY: Record<AutomationTrigger, AutomationEntity> = {
  'chat.created': 'conversation',
  'chat.label_added': 'conversation',
  'chat.label_removed': 'conversation',
  'message.received': 'message',
  'message.updated': 'message',
  'message.deleted': 'message',
  'message.flagged': 'message',
  'message.unflagged': 'message',
  'ticket.created': 'conversation',
  'ticket.updated': 'conversation',
  'ticket.closed': 'conversation',
  'ticket.label_added': 'conversation',
  'ticket.label_removed': 'conversation',
  'task.created': 'task',
  'task.assigned': 'task',
  'reaction.received': 'reaction',
  'member.joined': 'member',
  'member.role_changed': 'member',
}

export const MESSAGE_FIELDS: FieldDefinition[] = [
  { key: 'body', label: 'Message text', type: 'text' },
  { key: 'direction', label: 'Direction', type: 'text', options: ['inbound', 'outbound'] },
  { key: 'isPrivateNote', label: 'Is a private note', type: 'boolean', options: ['yes', 'no'] },
]

export const TASK_FIELDS: FieldDefinition[] = [
  { key: 'title', label: 'Title', type: 'text' },
  { key: 'priority', label: 'Priority', type: 'text', options: ['low', 'normal', 'high', 'urgent'] },
  { key: 'status', label: 'Status', type: 'text', options: ['open', 'done', 'cancelled'] },
  { key: 'assigneeUserId', label: 'Assignee', type: 'text' },
]

export const REACTION_FIELDS: FieldDefinition[] = [
  { key: 'emoji', label: 'Emoji', type: 'text' },
  { key: 'fromMe', label: 'Sent by this workspace', type: 'boolean', options: ['yes', 'no'] },
]

export const MEMBER_FIELDS: FieldDefinition[] = [
  { key: 'role', label: 'Role', type: 'text', options: ['owner', 'admin', 'agent', 'viewer'] },
  { key: 'previousRole', label: 'Previous role', type: 'text', options: ['owner', 'admin', 'agent', 'viewer'] },
]

export function fieldsForTrigger(trigger: AutomationTrigger): FieldDefinition[] {
  switch (TRIGGER_ENTITY[trigger]) {
    case 'conversation':
      return CONVERSATION_FIELDS
    case 'message':
      return MESSAGE_FIELDS
    case 'task':
      return TASK_FIELDS
    case 'reaction':
      return REACTION_FIELDS
    case 'member':
      return MEMBER_FIELDS
  }
}

/* ---------------------------------------------------------------- actions */

export type AutomationActionType =
  | 'send_message'
  | 'forward_message'
  | 'delete_message'
  | 'create_ticket'
  | 'assign_ticket'
  | 'assign_chat'
  | 'close_ticket'
  | 'add_ticket_label'
  | 'remove_ticket_label'
  | 'add_chat_label'
  | 'remove_chat_label'
  | 'send_email'
  | 'flag_message'
  | 'unflag_message'
  | 'notify_assignee'
  | 'notify_webhook'

export const ACTION_LABELS: Record<AutomationActionType, string> = {
  send_message: 'Send a message',
  forward_message: 'Forward the message',
  delete_message: 'Delete the message',
  create_ticket: 'Create a ticket',
  assign_ticket: 'Assign the ticket',
  assign_chat: 'Assign the chat',
  close_ticket: 'Close the ticket',
  add_ticket_label: 'Add a label to the ticket',
  remove_ticket_label: 'Remove a label from the ticket',
  add_chat_label: 'Add a label to the chat',
  remove_chat_label: 'Remove a label from the chat',
  send_email: 'Send an email',
  flag_message: 'Flag the message',
  unflag_message: 'Unflag the message',
  notify_assignee: 'Notify the assignee',
  notify_webhook: 'Send a webhook notification',
}

/** `assign_ticket`/`assign_chat`'s value can be a literal user id, or one of these two dynamic
 *  modes. */
export const ASSIGNMENT_MODES = ['round_robin', 'least_loaded'] as const
export type AssignmentMode = (typeof ASSIGNMENT_MODES)[number]
export function isAssignmentMode(value: string): value is AssignmentMode {
  return (ASSIGNMENT_MODES as readonly string[]).includes(value)
}

/** Which roster narrowing a dynamic `assign_ticket`/`assign_chat` mode respects before picking
 *  who's next. `shift` is the long-standing, always-on behaviour, kept as the default so a rule
 *  saved before this existed keeps assigning exactly as it did. `status` is the member's manual
 *  online/offline toggle; `both` requires a candidate to clear both narrowings. */
export const ASSIGNMENT_FILTERS = ['shift', 'status', 'both'] as const
export type AssignmentFilter = (typeof ASSIGNMENT_FILTERS)[number]

/**
 * Which actions make sense for which trigger. A "close the ticket" action on a trigger with
 * no ticket in scope (a bare chat-created event, say) has nothing to close — offering it would
 * be a control that silently does nothing when the rule actually runs.
 */
export const ACTIONS_FOR_ENTITY: Record<AutomationEntity, AutomationActionType[]> = {
  conversation: [
    'send_message',
    'forward_message',
    'create_ticket',
    'assign_ticket',
    'assign_chat',
    'close_ticket',
    'add_ticket_label',
    'remove_ticket_label',
    'add_chat_label',
    'remove_chat_label',
    'notify_assignee',
    'send_email',
  ],
  message: ['delete_message', 'flag_message', 'unflag_message', 'add_chat_label', 'remove_chat_label', 'send_email'],
  task: ['notify_assignee', 'send_email'],
  reaction: ['send_message', 'create_ticket', 'add_chat_label', 'remove_chat_label', 'send_email'],
  // No email provider is connected anywhere in this product (see `send_email` elsewhere), and
  // a member event has no assignee/ticket/chat to act on — an admin notification by email is
  // the one thing that generalises, honestly logged as failed until a provider exists.
  member: ['send_email'],
}

export interface AutomationAction {
  type: AutomationActionType
  /** Free text for `send_message`/`send_email`/`flag_message`; a label name for the label
   *  actions; a category for `create_ticket`; a user id OR `round_robin`/`least_loaded` for
   *  `assign_ticket`/`assign_chat`. One field, reused per type, same shape `@shared/segments`'s
   *  `Condition.value` already uses for the same reason. */
  value?: string
  /** `create_ticket` only. */
  priority?: 'low' | 'normal' | 'high' | 'urgent'
  /** `assign_ticket`/`assign_chat` only, and only when `value` is a dynamic mode rather than a
   *  specific user id. Unset means `'shift'`, preserving the pre-existing always-on-shift
   *  behaviour for rules saved before this field existed. */
  assignmentFilter?: AssignmentFilter
  /** Skip this action if it already fired for the same entity within this many seconds — a
   *  debounce, not a rate limit: it tracks the *entity* an action targeted (a chat, a ticket),
   *  not the rule as a whole, so a rule touching many chats still fires once per chat rather
   *  than being silenced globally by the first one. Unset or 0 means no cooldown, the existing
   *  behavior. */
  cooldownSeconds?: number
}

export interface AutomationRuleDraft {
  name: string
  trigger: AutomationTrigger
  match: 'all' | 'any'
  conditions: ConditionEntry[]
  actions: AutomationAction[]
  enabled: boolean
}

/** Whether a rule fires for the given subject — the condition half only, not the actions. */
export function ruleMatches(rule: { match: 'all' | 'any'; conditions: ConditionEntry[] }, subject: Record<string, unknown>): boolean {
  const segment: Segment = { match: rule.match, conditions: rule.conditions }
  return matchesSegment(segment, subject as never)
}

export function validateAutomationRule(draft: AutomationRuleDraft): string[] {
  const problems: string[] = []
  if (!draft.name.trim()) problems.push('Name the rule.')
  const hasLiveCondition = draft.conditions.some((entry) =>
    isConditionGroup(entry) ? entry.conditions.some((c) => c.field) : Boolean(entry.field),
  )
  if (!hasLiveCondition) {
    problems.push('Add at least one condition, or this will match nothing.')
  }
  if (draft.actions.length === 0) problems.push('Add at least one action.')

  const allowed = new Set(ACTIONS_FOR_ENTITY[TRIGGER_ENTITY[draft.trigger]])
  for (const action of draft.actions) {
    if (!allowed.has(action.type)) {
      problems.push(`"${ACTION_LABELS[action.type]}" does not apply to "${TRIGGER_LABELS[draft.trigger]}".`)
    }
    if ((action.type === 'send_message' || action.type === 'send_email' || action.type === 'add_chat_label' ||
      action.type === 'remove_chat_label' || action.type === 'add_ticket_label' || action.type === 'remove_ticket_label' ||
      action.type === 'create_ticket' || action.type === 'assign_ticket' || action.type === 'assign_chat') && !action.value?.trim()) {
      problems.push(`"${ACTION_LABELS[action.type]}" needs a value.`)
    }
    if (action.cooldownSeconds !== undefined && (!Number.isFinite(action.cooldownSeconds) || action.cooldownSeconds < 0)) {
      problems.push(`"${ACTION_LABELS[action.type]}"'s cooldown needs to be zero or a positive number of seconds.`)
    }
  }
  return problems
}

/* ---------------------------------------------------------------- templates */

/**
 * A starting point for the rule builder, not a rule itself — a new admin facing a blank
 * trigger/condition/action form has nothing to react against, and these are the shapes people
 * build by hand anyway (the same reasoning `CONTACT_PRESETS`/`VIEW_PRESETS` already follow for
 * segments). Every template is a complete, valid `AutomationRuleDraft` — it inserts straight
 * into the editor and is fully editable from there, never a fixed, unchangeable choice.
 */
export const TEMPLATE_CATEGORIES = [
  'Assignment',
  'Escalation',
  'Flagging',
  'Replies',
  'Notifications',
  'Ticketing',
] as const
export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number]

export interface AutomationTemplate {
  category: TemplateCategory
  name: string
  /** One line, shown under the template's name — what it does, not what it is. */
  description: string
  draft: Omit<AutomationRuleDraft, 'enabled'> & { delaySeconds?: number }
}

export const AUTOMATION_TEMPLATES: AutomationTemplate[] = [
  {
    category: 'Assignment',
    name: 'Round-robin new tickets',
    description: 'Every ticket that opens goes to whoever has waited longest since their last one.',
    draft: {
      name: 'Round-robin new tickets',
      trigger: 'ticket.created',
      match: 'all',
      conditions: [{ field: 'status', operator: 'is', value: 'open' }],
      actions: [{ type: 'assign_ticket', value: 'round_robin' }],
    },
  },
  {
    category: 'Assignment',
    name: 'Assign new chats round-robin',
    description: 'Every new direct chat gets an owner immediately, ticket or not.',
    draft: {
      name: 'Assign new chats round-robin',
      trigger: 'chat.created',
      match: 'all',
      conditions: [{ field: 'kind', operator: 'is', value: 'direct' }],
      actions: [{ type: 'assign_chat', value: 'round_robin' }],
    },
  },
  {
    category: 'Escalation',
    name: 'Escalate if still open after 15 minutes',
    description: "Checks again 15 minutes after a ticket opens, and notifies the assignee if it's still waiting.",
    draft: {
      name: 'Escalate if still open after 15 minutes',
      trigger: 'ticket.created',
      match: 'all',
      conditions: [{ field: 'status', operator: 'is', value: 'open' }],
      actions: [{ type: 'notify_assignee', value: 'This ticket is still open 15 minutes after it was opened.' }],
      delaySeconds: 900,
    },
  },
  {
    category: 'Flagging',
    name: 'Flag messages that mention cancelling',
    description: 'Puts a flag on any inbound message containing "cancel", for review.',
    draft: {
      name: 'Flag messages that mention cancelling',
      trigger: 'message.received',
      match: 'all',
      conditions: [{ field: 'body', operator: 'contains', value: 'cancel' }],
      actions: [{ type: 'flag_message', value: 'Possible cancellation' }],
    },
  },
  {
    category: 'Replies',
    name: 'Acknowledge every new ticket',
    description: 'Sends an immediate reply so nobody wonders whether their message went through.',
    draft: {
      name: 'Acknowledge every new ticket',
      trigger: 'ticket.created',
      match: 'all',
      conditions: [{ field: 'status', operator: 'is', value: 'open' }],
      actions: [{ type: 'send_message', value: "Thanks for reaching out — we'll get back to you shortly." }],
    },
  },
  {
    category: 'Notifications',
    name: 'Notify the assignee when a ticket is labelled urgent',
    description: 'The moment an "Urgent" label lands on a ticket, its assignee is told.',
    draft: {
      name: 'Notify the assignee when a ticket is labelled urgent',
      trigger: 'ticket.label_added',
      match: 'all',
      conditions: [{ field: 'labels', operator: 'contains', value: 'Urgent' }],
      actions: [{ type: 'notify_assignee', value: 'This ticket was just labelled urgent.' }],
    },
  },
  {
    category: 'Notifications',
    name: 'Welcome a new teammate',
    description: 'A starting point for a welcome email whenever someone joins the workspace.',
    draft: {
      name: 'Welcome a new teammate',
      trigger: 'member.joined',
      match: 'all',
      conditions: [{ field: 'role', operator: 'is_set' }],
      actions: [{ type: 'send_email', value: 'ops@example.com' }],
    },
  },
  {
    category: 'Ticketing',
    name: 'Label urgent tickets for visibility',
    description: 'Tags every urgent ticket with a "Priority" label the moment it opens.',
    draft: {
      name: 'Label urgent tickets for visibility',
      trigger: 'ticket.created',
      match: 'all',
      conditions: [{ field: 'priority', operator: 'is', value: 'urgent' }],
      actions: [{ type: 'add_ticket_label', value: 'Priority' }],
    },
  },
]

/**
 * A starting point for the flow builder — a multi-step chain, the way `AUTOMATION_TEMPLATES`
 * above is a starting point for one rule. Each step here becomes its own `automationRules` row
 * sharing the flow's trigger; the "wait, then re-check" step is `delayMinutes`, the same
 * `delaySeconds` field a hand-authored rule already has. Every condition used is a field the
 * engine actually tracks today (ticket status) rather than a "did the customer reply since our
 * last message" signal the engine has no way to answer yet — see FlowBuilder.tsx's own note on
 * that gap.
 */
export interface FlowTemplateStep {
  name?: string
  match: 'all' | 'any'
  conditions: ConditionEntry[]
  actions: AutomationAction[]
  /** Minutes to wait after the trigger before this step checks its conditions. 0 means immediately. */
  delayMinutes?: number
}

export interface FlowTemplate {
  name: string
  description: string
  trigger: AutomationTrigger
  steps: FlowTemplateStep[]
}

export const FLOW_TEMPLATES: FlowTemplate[] = [
  {
    name: 'Welcome, then follow up if still open',
    description: 'Greets every new ticket immediately, then nudges again 15 minutes later if nobody has resolved it yet.',
    trigger: 'ticket.created',
    steps: [
      {
        name: 'Send a welcome message',
        match: 'all',
        conditions: [{ field: 'status', operator: 'is', value: 'open' }],
        actions: [{ type: 'send_message', value: "Thanks for reaching out — we'll be right with you." }],
      },
      {
        name: 'Follow up if still open',
        match: 'all',
        conditions: [{ field: 'status', operator: 'is', value: 'open' }],
        delayMinutes: 15,
        actions: [{ type: 'send_message', value: "Just checking in — we're still working on this and will follow up shortly." }],
      },
    ],
  },
  {
    name: 'Assign, then escalate if unanswered',
    description: 'Assigns every new ticket round robin, then notifies the assignee if it is still open 15 minutes later.',
    trigger: 'ticket.created',
    steps: [
      {
        name: 'Assign round robin',
        match: 'all',
        conditions: [{ field: 'status', operator: 'is', value: 'open' }],
        actions: [{ type: 'assign_ticket', value: 'round_robin' }],
      },
      {
        name: 'Escalate if unresolved',
        match: 'all',
        conditions: [{ field: 'status', operator: 'is', value: 'open' }],
        delayMinutes: 15,
        actions: [{ type: 'notify_assignee', value: 'This ticket is still open 15 minutes after it was opened.' }],
      },
    ],
  },
]
